// BBB-457: legend swatch schema supports 'solid' | 'outline' | 'dotted' |
// 'outline-tint' | 'hatch' | 'stipple' so a swatch can visually match map
// rendering.
// Pure function, no React — trivially unit-testable.

import { DEFAULT_FILL_OPACITY, fillHexAlpha } from './fillOpacity';

// BBB-525: these keys are the CATALOGUE's hatch vocabulary, not a second one.
// They used to read 'forward' and 'backward' while data/categories/designations.js
// said 'slash' and 'backslash', and the two only agreed because legend-metadata.js
// wrote the swatch names by hand and never passed a catalogue `hatchDirection`
// through. Anyone wiring the next hatched layer straight from the catalogue got a
// silent fallback to a forward diagonal rather than an error. One vocabulary now,
// matching lib/adapters/svgHatchOverlay.js's HATCH_ANGLES, and an unknown
// direction throws instead of guessing.
const HATCH_GRADIENTS = {
  // Forward diagonal (England SSSI / shape://slash)
  slash:
    'repeating-linear-gradient(45deg, transparent, transparent 2px, CURRENT 2px, CURRENT 3.5px)',
  // Backward diagonal (England SAC / shape://backslash)
  backslash:
    'repeating-linear-gradient(-45deg, transparent, transparent 2px, CURRENT 2px, CURRENT 3.5px)',
  // Cross (England SPA / Scotland SSSI)
  cross: [
    'repeating-linear-gradient(45deg, transparent, transparent 2px, CURRENT 2px, CURRENT 3.5px)',
    'repeating-linear-gradient(-45deg, transparent, transparent 2px, CURRENT 2px, CURRENT 3.5px)',
  ].join(', '),
  // Vertical (Scotland SAC native)
  vertical:
    'repeating-linear-gradient(90deg, transparent, transparent 2px, CURRENT 2px, CURRENT 3.5px)',
  // Horizontal (Scotland SPA native)
  horizontal:
    'repeating-linear-gradient(0deg, transparent, transparent 2px, CURRENT 2px, CURRENT 3.5px)',
};

/**
 * The direction a hatch swatch draws when the caller names none. Matches
 * svgHatchOverlay.js's own `source.hatchDirection ?? 'slash'` default, so a
 * source that declares no direction gets the same diagonal on the map and in
 * the key.
 */
export const DEFAULT_HATCH_DIRECTION = 'slash';

/** The hatch directions a swatch can draw. Same vocabulary as the catalogue. */
export const HATCH_DIRECTIONS = Object.freeze(Object.keys(HATCH_GRADIENTS));

function withHatchColour(template, colour) {
  return template.replace(/CURRENT/g, colour);
}

/**
 * @param {{ colour?: string, type?: string, hatch?: string, fillOpacity?: number }} swatch
 *   fillOpacity must match the opacity the map draws for that source, or the key
 *   contradicts the map. Defaults to the designations' value; sources that
 *   override it in the catalogue must pass the same override here.
 * @returns {object} CSS style props for a legend swatch
 */
export function swatchStyle({ colour, type, hatch, fillOpacity } = {}) {
  // Derived, not hardcoded: these used to be the literals '1F' and '38', which is
  // how the swatch and the map were free to drift apart.
  const alpha = fillHexAlpha(fillOpacity ?? DEFAULT_FILL_OPACITY);

  switch (type) {
    // England vector layers: faint fill + a solid / dashed / dotted border, so
    // the swatch matches the rendered shape — the fill tells the farmer which
    // side of the boundary is inside, and the border pattern distinguishes
    // overlapping designations without relying on hue (BBB-457).
    //
    // The 2px border deliberately does NOT track the map's outlineWeight, which
    // is 3 for designations and 2.25 for the wide-area layers. What has to match
    // is the pattern and the colour; absolute pixels do not, because a swatch is
    // a ~16px box and a 3px border would swallow the tint it is meant to show.
    // Scaling it 1:1 would make the key less faithful, not more.
    case 'outline':
      return { backgroundColor: `${colour}${alpha}`, border: `2px solid ${colour}` };
    case 'dashed':
      return { backgroundColor: `${colour}${alpha}`, border: `2px dashed ${colour}` };
    case 'dotted':
      return { backgroundColor: `${colour}${alpha}`, border: `2px dotted ${colour}` };
    case 'outline-tint':
      // Tint + solid outline (Scotland NVZ treatment).
      return {
        backgroundColor: `${colour}${alpha}`,
        border: `2px solid ${colour}`,
      };
    case 'hatch': {
      // Undefined is a legitimate caller saying "the default diagonal"; a
      // direction that does not exist is a wiring mistake and is refused rather
      // than silently drawn as something else (BBB-525). Every caller is static
      // catalogue or legend data, so this fires in the test run, not at a
      // farmer's map.
      const direction = hatch || DEFAULT_HATCH_DIRECTION;
      const template = HATCH_GRADIENTS[direction];
      if (!template) {
        throw new Error(
          `swatchStyle: unknown hatch direction '${direction}'. Known directions: ${Object.keys(
            HATCH_GRADIENTS
          ).join(', ')}.`
        );
      }
      return {
        backgroundColor: 'transparent',
        backgroundImage: withHatchColour(template, colour),
        border: `1.5px solid ${colour}`,
      };
    }
    case 'stipple':
      // Defra's own drinking-water safeguard style: a black stipple over a white
      // fill, edged in the source's blue-grey. The dots are the fill here, which
      // is why this isn't the 'dotted' case — that dots the border instead.
      return {
        backgroundColor: '#FFFFFF',
        backgroundImage: 'radial-gradient(#000000 0.5px, transparent 0.6px)',
        backgroundSize: '3px 3px',
        border: `1.5px solid ${colour}`,
      };
    case 'solid':
    default:
      return { backgroundColor: colour };
  }
}
